import { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import './PrivacyWizard.css';

const PRIVATE_DEFAULTS = {
  military: 'private', clearance: 'private', certifications: 'private',
  skills: 'private', contact: 'private', resume: 'private',
};

const BRANCH_OPTIONS = [
  'U.S. Army', 'U.S. Navy', 'U.S. Air Force', 'U.S. Marine Corps',
  'U.S. Coast Guard', 'U.S. Space Force',
];

const STATUS_OPTIONS = [
  {
    id: 'actively',
    iconClass: 'green',
    title: 'Actively looking',
    desc: 'I am preparing for or actively pursuing my next role.',
  },
  {
    id: 'open',
    iconClass: 'gold',
    title: 'Open to conversations',
    desc: "I'm exploring options but not in a rush.",
  },
  {
    id: 'notlooking',
    iconClass: 'muted',
    title: 'Not looking right now',
    desc: 'I am here for community, learning, or future planning.',
  },
];

const VIS_SECTIONS = [
  { id: 'military',      label: 'Military Experience', desc: 'Branch, rank, years, AFSC/MOS' },
  { id: 'clearance',     label: 'Clearance Tier',      desc: 'Secret, TS, TS/SCI — self-reported' },
  { id: 'certifications',label: 'Certifications',      desc: 'CompTIA, DoD, industry certs' },
  { id: 'skills',        label: 'Skills',              desc: 'Technical and leadership skills' },
  { id: 'contact',       label: 'Contact Info',        desc: 'Email and phone number' },
  { id: 'resume',        label: 'Resume',              desc: 'Downloadable PDF or DOCX' },
];

const LEVEL_LABELS = {
  recruiters: 'Employers',
  members:    'Members',
  private:    'Private',
};

const STATUS_SUMMARY = {
  actively:   'Actively looking for work',
  open:       'Open to conversations',
  notlooking: 'Not looking right now',
};

export default function PrivacyWizard({ onComplete, userId }) {
  const [isOpen,     setIsOpen]     = useState(false);
  const [step,       setStep]       = useState(1);
  const [status,     setStatus]     = useState(null);
  const [visibility, setVisibility] = useState(PRIVATE_DEFAULTS);
  const [service, setService] = useState({ branch: '', rank: '', yearsOfService: '', occupationCode: '', occupationTitle: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const checkedRef = useRef(false);

  useEffect(() => {
    async function checkWizard() {
      if (checkedRef.current) return;
      if (!userId) return;
      const { data, error } = await supabase
        .from('profiles')
        .select('wizard_complete, branch, rank, years_of_service, occupation_code, occupation_title, looking_status, visibility')
        .eq('id', userId)
        .single();
      if (!error && data && data.wizard_complete) return;
      if (!error && data) {
        setService({
          branch: data.branch || '', rank: data.rank || '',
          yearsOfService: data.years_of_service || '',
          occupationCode: data.occupation_code || '', occupationTitle: data.occupation_title || '',
        });
        setStatus(data.looking_status || null);
        setVisibility(data.visibility || PRIVATE_DEFAULTS);
      }
      checkedRef.current = true;
      const t = setTimeout(() => setIsOpen(true), 600);
      return () => clearTimeout(t);
    }
    checkWizard();
  }, [userId]);

  const handleStatusSelect = (id) => {
    setStatus(id);
  };

  const handleVisChange = (section, level) => {
    setVisibility(prev => ({ ...prev, [section]: level }));
  };

  const handleNext = () => {
    if (step < 4) setStep(s => s + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(s => s - 1);
  };

  const handleComplete = async () => {
    setSaving(true);
    setSaveError('');
    if (userId) {
      const { error } = await supabase
        .from('profiles')
        .update({
          wizard_complete: true, looking_status: status, visibility,
          branch: service.branch || null,
          rank: service.rank.trim() || null,
          years_of_service: service.yearsOfService ? Number(service.yearsOfService) : 0,
          occupation_branch: service.branch || null,
          occupation_code: service.occupationCode.trim() || null,
          occupation_title: service.occupationTitle.trim() || null,
        })
        .eq('id', userId);
      if (error) {
        setSaveError('We could not save your profile. Your choices have not been applied. Please try again.');
        setSaving(false);
        return;
      }
    }
    setIsOpen(false);
    setSaving(false);
    if (onComplete) onComplete({ status, visibility });
  };

  const handleSkip = async () => {
    if (saving) return;
    setSaving(true);
    setSaveError('');
    try {
      if (!userId) throw new Error('Missing member session');
      const { data, error } = await supabase
        .from('profiles')
        .update({ wizard_complete: true, looking_status: null, visibility: PRIVATE_DEFAULTS })
        .eq('id', userId)
        .select('id')
        .single();
      if (error || !data || data.id !== userId) throw new Error('Save not confirmed');
      setIsOpen(false);
    } catch {
      setSaveError('We could not confirm your privacy settings were saved. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const VisBtn = ({ section, level }) => (
    <button
      className={`pw-vis-btn ${visibility[section] === level ? `active ${level}` : ''}`}
      onClick={() => handleVisChange(section, level)}
    >
      {LEVEL_LABELS[level]}
    </button>
  );

  // Closed onboarding must not expose invisible controls to keyboard or screen readers.
  if (!isOpen) return null;

  return (
    <div
      className="pw-overlay open"
      onClick={(e) => { if (e.target === e.currentTarget) {} }}
    >
      <div className="pw-modal">

        <div className="pw-progress">
          <div className={`pw-step ${step >= 1 ? 'active' : ''}`} />
          <div className={`pw-step ${step >= 2 ? 'active' : ''}`} />
          <div className={`pw-step ${step >= 3 ? 'active' : ''}`} />
          <div className={`pw-step ${step >= 4 ? 'active' : ''}`} />
          <span className="pw-step-label">Step {step} of 4</span>
        </div>

        {step === 1 && (
          <div className="pw-screen">
            <div className="pw-eyebrow">Your service, in your words</div>
            <h2 className="pw-title">Start with the background that shaped you.</h2>
            <p className="pw-subtitle">
              These details build your Mission Background. We do not infer duties, credentials,
              or skills from them, and everything stays private until you choose an audience.
            </p>
            <div className="pw-service-form">
              <label>Branch of service
                <select value={service.branch} onChange={e => setService(s => ({ ...s, branch: e.target.value }))}>
                  <option value="">Select a branch</option>
                  {BRANCH_OPTIONS.map(branch => <option key={branch}>{branch}</option>)}
                </select>
              </label>
              <div className="pw-service-row">
                <label>Highest rank or grade <span>Optional</span>
                  <input value={service.rank} onChange={e => setService(s => ({ ...s, rank: e.target.value }))} placeholder="Use the title you recognize" />
                </label>
                <label>Years served <span>Optional</span>
                  <input type="number" min="0" max="50" value={service.yearsOfService} onChange={e => setService(s => ({ ...s, yearsOfService: e.target.value }))} placeholder="e.g. 8" />
                </label>
              </div>
              <div className="pw-service-row">
                <label>Specialty code <span>Optional</span>
                  <input value={service.occupationCode} onChange={e => setService(s => ({ ...s, occupationCode: e.target.value }))} placeholder="MOS, AFSC, rating, or designator" />
                </label>
                <label>Specialty title <span>Optional</span>
                  <input value={service.occupationTitle} onChange={e => setService(s => ({ ...s, occupationTitle: e.target.value }))} placeholder="Your service title" />
                </label>
              </div>
            </div>
            <div className="pw-use-note"><strong>How this is used:</strong> to organize the service story on your profile. Nothing here is treated as verified unless VetQualified explicitly says so.</div>
            <div className="pw-footer"><button className="pw-next-btn" onClick={handleNext} disabled={!service.branch || saving}>Continue →</button></div>
            {saveError && <div className="pw-error" role="alert">{saveError}</div>}
            <button type="button" className="pw-skip" onClick={handleSkip} disabled={saving}>
              {saving ? 'Saving…' : 'Skip for now — keep my profile private'}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="pw-screen">
            <div className="pw-eyebrow">Your next chapter</div>
            <h2 className="pw-title">What brings you to VetQualified today?</h2>
            <p className="pw-subtitle">This helps shape your experience. It does not change who can see your information.</p>
            <div className="pw-status-cards">
              {STATUS_OPTIONS.map(({ id, iconClass, title, desc }) => (
                <div
                  key={id}
                  className={`pw-status-card${status === id ? ' selected' : ''}`}
                  onClick={() => handleStatusSelect(id)}
                >
                  <div className={`pw-status-icon ${iconClass}`} aria-hidden="true" />
                  <div className="pw-status-text">
                    <h4>{title}</h4>
                    <p>{desc}</p>
                  </div>
                  <div className="pw-radio">
                    <div className="pw-radio-dot" />
                  </div>
                </div>
              ))}
            </div>
            <div className="pw-footer">
              <button className="pw-back-btn" onClick={handleBack}>← Back</button>
              <button className="pw-next-btn" onClick={handleNext} disabled={!status}>
                Continue →
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="pw-screen">
            <div className="pw-eyebrow">Privacy Setup</div>
            <h2 className="pw-title">Who can see each part of your profile?</h2>
            <p className="pw-subtitle">
              Every category starts private. Choose Member-visible or Employer-shared only
              when you want that audience to see it.
            </p>
            <div className="pw-vis-rows">
              {VIS_SECTIONS.map(({ id, label, desc }) => (
                <div className="pw-vis-item" key={id}>
                  <div className="pw-vis-info">
                    <h4>{label}</h4>
                    <p>{desc}</p>
                  </div>
                  <div className="pw-vis-select">
                    <VisBtn section={id} level="recruiters" />
                    <VisBtn section={id} level="members"    />
                    <VisBtn section={id} level="private"    />
                  </div>
                </div>
              ))}
            </div>
            <div className="pw-footer">
              <button className="pw-back-btn" onClick={handleBack}>← Back</button>
              <button className="pw-next-btn" onClick={handleNext}>
                Review my choices →
              </button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="pw-screen">
            <div className="pw-eyebrow">Privacy Setup</div>
            <h2 className="pw-title">Here's exactly what people will see.</h2>
            <p className="pw-subtitle">
              Review your choices below. Hit confirm and you're in.
            </p>
            <div className="pw-summary">
              <div className="pw-summary-row">
                <span className="pw-summary-icon">📌</span>
                <span className="pw-summary-text">
                  Your status is set to <strong>{STATUS_SUMMARY[status]}</strong>.
                </span>
              </div>
              {VIS_SECTIONS.map(({ id, label }) => (
                <div className="pw-summary-row" key={id}>
                  <span className="pw-summary-icon" aria-hidden="true">•</span>
                  <span className="pw-summary-text">
                    <strong>{label}</strong> is visible to{' '}
                    <strong>
                      {visibility[id] === 'recruiters' ? 'employers through the existing employer view' :
                       visibility[id] === 'members'    ? 'VetQualified members' :
                                                         'only you'}
                    </strong>.
                  </span>
                </div>
              ))}
            </div>
            <p className="pw-summary-note">
              Private means only you. Member-visible does not include employers. Employer-shared
              means the field can appear in the existing employer search experience. Contact info
              and resumes remain private unless you take an existing explicit sharing action.
            </p>
            {saveError && <div className="pw-error" role="alert">{saveError}</div>}
            <div className="pw-footer">
              <button className="pw-back-btn" onClick={handleBack}>← Back</button>
              <button className="pw-next-btn" onClick={handleComplete} disabled={saving}>
                {saving ? 'Saving…' : 'Confirm — show my Mission Background'}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
